import { type ZodType, type ZodTypeDef, z } from 'zod'
import { requestExplorerJson } from '../request'

export type EtherscanV2Client = {
  get: <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(parameters: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }) => Promise<Output>
}

const baseUrl = 'https://api.etherscan.io/v2/api'

type CreateEtherscanV2ClientParameters = {
  chainId: number
  apiKey?: string
  proxyUrl?: string
}

export const createEtherscanV2Client = ({
  chainId,
  apiKey,
  proxyUrl,
}: CreateEtherscanV2ClientParameters): EtherscanV2Client => {
  // apiKey = undefined かつ proxyUrl = undefined の場合はエラー
  if (apiKey === undefined && proxyUrl === undefined) {
    throw new Error('apiKey or proxyUrl must be provided')
  }

  const get = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >({
    path,
    query,
    resultSchema,
    headers,
  }: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }): Promise<Output> => {
    // define the schema for the response
    const responseSchema = z.object({
      status: z.string(),
      message: z.string(),
      result: z.unknown(),
    })

    // request
    const url = new URL(path, baseUrl)
    url.searchParams.set('chainid', chainId.toString())
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value)
    }
    if (apiKey !== undefined) {
      url.searchParams.set('apikey', apiKey)
    }
    console.debug(`[GET] ${url.toString()}`)
    const { status, message, result } = responseSchema.parse(
      await requestExplorerJson({
        url:
          proxyUrl === undefined
            ? url.toString()
            : `${proxyUrl}${encodeURIComponent(url.toString())}`,
        key: `etherscan:${proxyUrl ?? apiKey}`,
        intervalMs: 400,
        headers,
      }),
    )
    if (status !== '1') {
      throw typeof result === 'string' ? new Error(result) : new Error(message)
    }
    if (result === null || result === undefined) {
      throw new Error(message)
    }
    if (typeof result === 'string') {
      throw new TypeError(result)
    }
    return resultSchema.parse(result)
  }

  return { get }
}
